import { useEffect, useState } from 'react';
import { AppState } from 'react-native';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import type { UserPresence } from '../types';

const HEARTBEAT_INTERVAL_MS = 60_000;

async function writePresence(isOnline: boolean): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    await setDoc(
      doc(firestore, FIRESTORE_COLLECTIONS.users, uid),
      { presence: { isOnline, lastActiveAt: serverTimestamp() } },
      { merge: true },
    );
  } catch (error) {
    console.warn('[Tonyte][RN][Presence] failed to write presence', error);
  }
}

/**
 * Mount once at the app root (mirrors LocationEngineProvider's lifecycle
 * pattern). Marks the signed-in user online while the app is foregrounded,
 * with a periodic heartbeat so "last seen" stays meaningful, and offline the
 * moment the app backgrounds. Self-writable field on the user's own profile
 * doc — no new collection, no rules change needed.
 */
export function usePresenceHeartbeat(): void {
  useEffect(() => {
    void writePresence(true);
    const heartbeat = setInterval(() => {
      if (AppState.currentState === 'active') void writePresence(true);
    }, HEARTBEAT_INTERVAL_MS);

    const subscription = AppState.addEventListener('change', status => {
      void writePresence(status === 'active');
    });

    return () => {
      clearInterval(heartbeat);
      subscription.remove();
      void writePresence(false);
    };
  }, []);
}

/** Live-subscribes to another user's presence field — for the 1:1 chat header. */
export function useUserPresence(userId: string | null): UserPresence | null {
  const [presence, setPresence] = useState<UserPresence | null>(null);

  useEffect(() => {
    if (!userId) {
      setPresence(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.users, userId),
      snapshot => {
        const data = snapshot.data();
        const raw = data?.presence;
        setPresence(raw ? { isOnline: raw.isOnline === true, lastActiveAt: raw.lastActiveAt ?? null } : null);
      },
      error => {
        console.warn('[Tonyte][RN][Presence] failed to read presence', { userId, error });
      },
    );
    return unsub;
  }, [userId]);

  return presence;
}
